import { useMemo } from 'react';

import { Select } from 'antd';
import type { TooltipComponentOption } from 'echarts';
import { z } from 'zod';

import { formatDaysToDateRange } from '@/shared/lib/format';
import { calPercent, formatMetricValue } from '@/shared/lib/utils/number';
import { BarChart } from '@/shared/ui/BarChart';

import { QUALITY_CHART_COLOR_MAP, useQualityTrendQuery } from '@/entities/quality';

import { groupByDateTotals } from '@/features/quality/lib/getGroupByTotal';
import { useDataZoneStorageSelect, useDateOption } from '@/features/quality/model';
import { CardHeader } from '@/features/quality/ui/dashboard/CardHeader';

import styles from './DataZoneLoadStatus.module.css';

const TITLE_MAP = {
  total: {
    title: '누적 Row Count (Table 기준)',
  },
  daily: {
    title: '신규 데이터 품질 추이',
  },
};

const tooltipParamSchema = z.object({
  axisValue: z.string(),
  data: z.record(z.string(), z.union([z.number(), z.string()])),
});

const customTooltipFormatter = (params: TooltipComponentOption) => {
  if (Array.isArray(params)) {
    const parsed = tooltipParamSchema.safeParse(params[0]);
    if (!parsed.success) return '';

    const { axisValue, data: firstParamData } = parsed.data;
    let result = `${axisValue}<br/>`;

    const total = Object.values(firstParamData).reduce<number>((acc, value) => {
      if (typeof value === 'number') {
        return acc + value;
      }
      return acc;
    }, 0);

    Object.entries(firstParamData).forEach(([key, value]) => {
      if (typeof value === 'number') {
        const percent = calPercent(value, total);
        result += `${key}: ${formatMetricValue(value)} (${percent}%)<br/>`;
      }
    });
    return result;
  }
  return '';
};

export const DataZoneLoadStatus = () => {
  const {
    DATA_ZONE_STORAGE_TYPE_OPTIONS,
    selectedDataZoneStorageValue,
    selectedDataZoneStorageTypeValue,
    handleSelectDataZoneStorageTypeValue,
  } = useDataZoneStorageSelect();
  const { title } = TITLE_MAP[selectedDataZoneStorageValue as keyof typeof TITLE_MAP];

  const { DATE_OPTIONS, selectedDateValue, handleSelectDateValue } = useDateOption();
  const { startDate, endDate } = formatDaysToDateRange(selectedDateValue, 1);

  const dataZone = useMemo(() => {
    if (selectedDataZoneStorageTypeValue === 'all') return undefined;
    return selectedDataZoneStorageTypeValue;
  }, [selectedDataZoneStorageTypeValue]);

  const { data: apiResponse, isLoading: showLoading } = useQualityTrendQuery({
    fromDate: startDate,
    toDate: endDate,
    dataZone,
  });

  const chartData = useMemo(() => {
    if (!apiResponse?.data) return [];

    if (selectedDataZoneStorageTypeValue === 'all') {
      const groupedData = groupByDateTotals(apiResponse.data);
      return Object.entries(groupedData).map(([statDate, rows]) => ({
        statDate,
        양호: rows.good,
        미흡: rows.poor,
        '판정 불가': rows.unknown,
      }));
    }

    return apiResponse.data.map((item) => ({
      statDate: item.statDate,
      양호: item.goodRows,
      미흡: item.poorRows,
      '판정 불가': item.unknownRows,
    }));
  }, [apiResponse, selectedDataZoneStorageTypeValue]);

  return (
    <>
      <CardHeader
        title={title}
        rightContent={
          <>
            <Select
              className={styles.dataZoneSelect}
              options={DATA_ZONE_STORAGE_TYPE_OPTIONS}
              value={selectedDataZoneStorageTypeValue}
              onChange={handleSelectDataZoneStorageTypeValue}
            />
            <Select
              className={styles.timeSelect}
              options={DATE_OPTIONS}
              value={selectedDateValue}
              onChange={handleSelectDateValue}
            />
          </>
        }
      />
      <BarChart
        data={chartData}
        showLoading={showLoading}
        xAxisKey="statDate"
        isLegendVisible={true}
        emptyDescription="신규 데이터 품질이 없습니다."
        colorMap={QUALITY_CHART_COLOR_MAP}
        customTooltipFormatter={customTooltipFormatter}
      />
    </>
  );
};
